import fetTypes from './fetTypes/ar.js';
import transistorTech from './transistorTech/ar.js';

export default {
  fetTypes,
  transistorTech,
  meta: {
    code: 'ar',
    name: 'Arabic',
    nativeName: 'العربية',
    dir: 'rtl',
    flag: '🇸🇦'
  },
  header: {
    title: 'تعلّم فيزياء أشباه الموصلات',
    subtitle: 'محاكي تعليمي - نشر العلوم والفيزياء المتقدمة',
    visitors: 'الزوار'
  },
  footer: {
    model: 'النموذج الفيزيائي: تقريب النطاق المكافئ، تأيّن كامل للشوائب فوق درجة حرارة التجميد، شبه موصل غير منحل. صيغة فارشني لـ Eg(T). ثوابت النظام الدولي.',
    nonprofit: 'مشروع غير ربحي',
    partOf: 'جزء من',
    network: 'شبكة مواقع Basicão da Eletrônica',
    copyright: 'تعليمي CC BY-SA 4.0 · React + SVG + KaTeX'
  },
  menu: {
    categories: {
      learning: '📚 التعلّم',
      fundamentals: '🔬 الأساسيات',
      bandStructure: '⚛️ بنية النطاقات',
      quantumStatistics: '📊 الإحصاء الكمّي',
      devices: '⚡ الأجهزة',
      reference: '📐 المرجع'
    },
    items: {
      objectives: '🎯 خارطة الدراسة',
      conceptsQ: '📖 مفاهيم الأسئلة',
      questions: '❓ الأسئلة',
      glossary: '📚 المسرد',
      overview: 'نظرة عامة',
      lattice: 'الشبكة البلورية',
      'diamond-unit-cell': '💎 خلية الماس الواحدة',
      'crystal-structures': '💎 البنى البلورية',
      atomband: 'الذرات ← النطاقات',
      quantumAtom: '⚛️ النموذج الكمي للذرة',
      allowed: 'النطاقات المسموحة/الممنوعة',
      kp: 'كرونيج-بيني',
      mis: 'معدن × عازل × شبه موصل',
      kspace: 'فضاء-k (Si × GaAs)',
      effmass: 'الكتلة الفعّالة',
      particles: 'إلكترون × فجوة',
      fermi: 'فيرمي-ديراك و MB',
      'fermi-edu': '📊 فيرمي-ديراك التعليمي',
      dos: 'كثافة الحالات',
      'dos-edu': '📊 DOS التعليمي',
      arrhenius: 'n(T) أرينيوس',
      junction: '⚡ وصلة PN',
      transistorTech: '🔬 تقنيات الترانزستور',
      fetTypes: '🧪 أنواع ترانزستور FET',
      czochralski: '🔬 طرق النمو',
      perovskites: '☀️ بيروفسكيت ثنائي الأبعاد',
      exercises: '📝 التمارين',
      periodic: '⚛️ الجدول الدوري',
      about: '👤 حول',
      'support-us': '💖 ادعمنا',
      community: '🤝 ساهم على GitHub',
      community_title: 'انضم إلى منتدى المناقشات على GitHub — يتطلب حساب GitHub للمساهمة'
    },
    quantumAtom: '⚛️ النموذج الذري الكمي'
  },
  quantumAtom: {
    title: 'النموذج الذري الكمي',
    subtitle: 'الرؤية الحديثة للذرة وفق ميكانيكا الكم',
    alt: 'نموذج ذري كمي يُظهر النواة وسحابة الإلكترون والمدارات',
    sections: {
      nucleus: {
        title: 'النواة الذرية',
        content: 'تحتوي النواة على بروتونات (شحنة موجبة) ونيوترونات (بلا شحنة)، وتمثل معظم كتلة الذرة. وتترابط الجسيمات داخل النواة بفعل القوة النووية الشديدة.'
      },
      electronCloud: {
        title: 'سحابة الإلكترون',
        content: 'على عكس نموذج بور، لا تتبع الإلكترونات مدارات ثابتة. بل تُوصف بسحابة احتمالية — وهي مناطق يزداد فيها احتمال العثور على الإلكترون. وتُمثَّل هذه السحابة بالدالة الموجية ψ(r).'
      },
      orbitals: {
        title: 'المدارات الذرية',
        content: 'المدارات هي مناطق من الفضاء يكون فيها احتمال العثور على إلكترون مرتفعًا. ولكل مدار شكل مميز: المدارات s كروية، والمدارات p على شكل دمبل، والمدارات d أكثر تعقيدًا، وهكذا. ويتحدد الشكل بالأعداد الكمية.'
      },
      waveFunction: {
        title: 'الدالة الموجية',
        content: 'تُوصف الإلكترونات رياضيًا بدوال موجية ψ(x,y,z,t). ويمثل مربع الدالة الموجية |ψ|² كثافة احتمال العثور على الإلكترون في موضع معيّن. وهذا أساس تفسير بورن لميكانيكا الكم.'
      },
      uncertainty: {
        title: 'مبدأ عدم اليقين',
        content: 'صاغه فيرنر هايزنبرغ، وينص هذا المبدأ على استحالة المعرفة المتزامنة بدقة اعتباطية لموضع الجسيم وزخمه معًا. Δx·Δp ≥ ℏ/2. أي أنه كلما عرفنا الموضع بدقة أكبر، قلّت معرفتنا بالزخم، والعكس صحيح.'
      }
    },
    comparison: {
      title: 'تطور النماذج الذرية',
      rutherford: {
        title: 'نموذج رذرفورد (1911)',
        items: [
          'الذرة كالنظام الشمسي: نواة كثيفة وإلكترونات تدور حولها',
          'اكتشاف النواة الذرية عبر تجربة جسيمات ألفا',
          'إلكترونات في مدارات اعتباطية (دون تكميم)',
          'لم يفسر استقرار الذرة (كان ينبغي أن تسقط الإلكترونات في النواة)',
          'غير متوافق مع الأطياف الذرية المرصودة'
        ]
      },
      bohr: {
        title: 'نموذج بور (1913)',
        items: [
          'إلكترونات في مدارات دائرية ثابتة ومكمَّمة',
          'افترض مدارات مستقرة دون إشعاع',
          'تكميم الطاقة في مستويات منفصلة',
          'فسّر طيف الهيدروجين، لكن ليس الذرات التي تحوي أكثر من إلكترون',
          'مزيج من الفيزياء الكلاسيكية والكمية (شبه كلاسيكي)'
        ]
      },
      quantum: {
        title: 'النموذج الكمي (1926 حتى الآن)',
        items: [
          'إلكترونات في سحابة احتمالية',
          'الموضع موصوف بتوزيع احتمالي',
          'التكميم عبر الأعداد الكمية (n، l، m، s)',
          'يفسر جميع الذرات والجزيئات',
          'متوافق مع مبدأ عدم اليقين'
        ]
      }
    }
  },
  controlPanel: {
    title: '⚙️ معاملات النظام'
  },
  controls: {
    material: 'المادة',
    dopingType: 'نوع التشويب',
    intrinsic: 'ذاتي',
    ntype: 'نوع n (P)',
    ptype: 'نوع p (B)',
    temperature: 'درجة الحرارة',
    donorConcentration: 'N_D (الفوسفور)',
    acceptorConcentration: 'N_A (البورون)',
    manualFermi: 'تعيين E_F يدويًا (eV)'
  },
  carrierPanel: {
    title: 'القيم العددية المحسوبة',
    material: 'المادة',
    hint: 'لاحظ أن {formula} يُحافظ عليه (قانون تأثير الكتلة) بغض النظر عن التشويب، طالما بقي شبه الموصل غير منحلّ (E_F على الأقل 3kT من حواف النطاق).'
  },
  common: {
    back: 'رجوع',
    next: 'التالي',
    previous: 'السابق',
    close: 'إغلاق',
    loading: 'جاري التحميل...',
    error: 'خطأ',
    search: 'بحث',
    more: 'المزيد',
    less: 'أقل',
    showMore: 'عرض المزيد',
    showLess: 'عرض أقل',
    goTo: 'الذهاب إلى'
  },
  graphs: {
    title: 'رسم بياني',
    details: 'التفاصيل',
    formula: 'صيغة',
    description: 'الوصف',
    concepts: 'المفاهيم',
    symbols: 'الرموز',
    symbol: 'رمز',
    showDetails: 'عرض التفاصيل',
    hideDetails: 'إخفاء التفاصيل',
    replayAnimation: 'إعادة تشغيل الرسوم المتحركة',
    PhotonEnergyGraph: 'طاقة الفوتون',
    HeisenbergUncertaintyGraph: 'مبدأ عدم اليقين لهايزنبرغ',
    DensityOfStatesGraph: 'كثافة الحالات',
    BandStructureGraph: 'هيكل النطاقات'
  },
  learningObjectives: {
    title: 'خارطة الدراسة — 8 كفايات للتطوير',
    intro: 'بعد استكشاف هذا النظام، ينبغي أن تكون قادرًا على مناقشة كل من البنود أدناه. استخدم الأزرار للانتقال مباشرة إلى علامة التبويب التي توضّح كل مفهوم.',
    selfAssessment: 'التقييم الذاتي:',
    selfAssessmentBody: 'حاول صياغة إجابة مكونة من 2-3 جمل بصوت عالٍ لكل بند قبل مراجعة علامة التبويب. إذا استطعت شرحها جميعًا، فقد أتقنت أساسيات نظرية النطاقات في أشباه الموصلات.',
    items: {
      q1: {
        title: 'مناقشة النطاقات المسموحة والممنوعة، نوعيًا وبدقة (كرونيج-بيني)',
        hints: [
          'نوعيًا: قرّب الذرات في لوحة "الذرات ← النطاقات" وراقب كيف تنقسم المستويات.',
          'دقيقًا: في علامة تبويب "كرونيج-بيني" غيّر P وراقب المعادلة P·sin(αa)/(αa) + cos(αa) = cos(ka) تولّد نطاقات حيث |LHS| ≤ 1.',
          'في "النطاقات المسموحة/الممنوعة" لاحظ البنية الكاملة للبلورة مع نطاقات اللب ونطاق التكافؤ والفجوة ونطاق التوصيل.'
        ]
      },
      q2: {
        title: 'مناقشة انشطار مستويات الطاقة في السيليكون',
        hints: [
          'حرّك شريط التمرير r من 1 (ذرات معزولة) إلى 0 (بلورة). تنقسم مستويات 3s و 3p أولاً.',
          'زِد N لترى أنه مع 10²³ ذرة يصبح الانشطار مستمرًا تقريبًا.',
          'تنشأ الفجوة E_g ≈ 1.12 eV بين قمة النطاق الناشئ من 3s وقاع نطاق التوصيل الناشئ من 3p.'
        ]
      },
      q3: {
        title: 'تعريف الكتلة الفعّالة من مخطط E×k ومعناها للحركة',
        hints: [
          '1/m* = (1/ℏ²)(d²E/dk²): مقلوب انحناء النطاق عند النقطة القصوى.',
          'في "الكتلة الفعّالة" طبّق قوة F ولاحظ الجسيم الحر (m₀) وجسيم البلورة (m*) يتسارعان بشكل مختلف.',
          'في "فضاء-k" بدّل بين Si و GaAs: GaAs لديها m*_n = 0.067 m₀ ← إلكترونات أخف وأسرع بكثير.'
        ]
      },
      q4: {
        title: 'مناقشة مفهوم الفجوة (hole)',
        hints: [
          'في "إلكترون × فجوة": شحنة +q، كتلة فعّالة m*_p > 0، تقيم في أعلى نطاق التكافؤ.',
          'تخضع الفجوة لـ F = m*_p · a بشحنة +q لأن الكتلة معرّفة بـ m*_p = −ℏ²/(d²E/dk²) مع d²E/dk² < 0 في قمة نطاق التكافؤ.',
          'في "الشبكة البلورية" من النوع p، لاحظ الحالة الفارغة في الرابطة التي تنقصها إلكترون عندما يستبدل البورون السيليكون.'
        ]
      },
      q5: {
        title: 'مناقشة خصائص الفجوات المباشرة مقابل غير المباشرة',
        hints: [
          'في "فضاء-k" بدّل المادة:',
          '• GaAs (فجوة مباشرة، Γ): قاع نطاق التوصيل وقمة نطاق التكافؤ عند نفس k. انتقال إشعاعي بفوتون فقط ← مناسب للدايودات الباعثة للضوء والليزر.',
          '• Si/Ge (فجوة غير مباشرة): قاع نطاق التوصيل منزاح في k. يحتاج الانتقال إلى فوتون + فونون ← إصدار ضوئي غير فعّال.'
        ]
      },
      q6: {
        title: 'التمييز بين معدن × عازل × شبه موصل من خلال نطاقات الطاقة',
        hints: [
          'في "معدن × عازل × شبه موصل":',
          '• معدن: E_F داخل نطاق مسموح ← حالات فارغة متاحة فورًا.',
          '• شبه موصل: فجوة صغيرة (~1 eV)، σ ↑ مع T (الإثارة الحرارية تولّد حاملات).',
          '• عازل: فجوة كبيرة (>5 eV)، σ مهمل حتى عند 300 K.'
        ]
      },
      q7: {
        title: 'تعريف وحساب دالة كثافة الحالات g(E)',
        hints: [
          'النموذج: بئر لانهائي ثلاثي الأبعاد ⇒ E = ℏ²π²(n_x²+n_y²+n_z²)/(2mL²).',
          'في "كثافة الحالات" تُظهر لوحة QuantumWell3D الحالات كنقاط منفصلة في فضاء-k؛ زد نصف قطر كرة فيرمي وقارن العدد المتقطع بالتنبؤ المستمر.',
          'النتيجة: g(E) = (1/2π²)(2m*/ℏ²)^(3/2) √(E−E_c) لنطاق التوصيل.'
        ]
      },
      q8: {
        title: 'فهم توزيع فيرمي-ديراك وطاقة فيرمي',
        hints: [
          'f(E) = 1 / [1 + exp((E−E_F)/k_BT)] تُعطي احتمال شغل حالة طاقة E.',
          'عند T = 0 K: f تمثّل درجة مثالية عند E_F. عند T > 0: انتقال سلس بعرض ~ k_BT.',
          'E_F هي الكمون الكيميائي للإلكترونات. في معدن تقع داخل النطاق؛ في شبه موصل ذاتي تقع قرب منتصف الفجوة.',
          'لـ |E − E_F| ≫ k_BT، f(E) ≈ exp(−(E−E_F)/k_BT) — حد ماكسويل-بولتزمان.'
        ]
      }
    }
  },
  questions: {
    title: '❓ الأسئلة — التقييم الذاتي',
    modeOne: '📇 واحدة في كل مرة',
    modeAll: '📋 الكل',
    stats: {
      pending: 'معلّقة',
      reset: '♻️ إعادة ضبط',
      resetTitle: 'إعادة ضبط الكل'
    },
    progress: 'السؤال {current} من {total}',
    nav: {
      previous: '→ السابق',
      next: 'التالي ←'
    },
    card: {
      prompt1: '1. حاول صياغة إجابتك ذهنيًا خلال 30 ثانية.',
      prompt2: '2. اسرد النقاط الرئيسية التي ستضمّنها.',
      prompt3: '3. عندما تكون جاهزًا، انقر أدناه للتحقق.',
      show: '👁️ إظهار الإجابة',
      hide: '🔁 إخفاء',
      modelAnswer: 'الإجابة النموذجية:',
      keyPoints: 'النقاط الرئيسية:',
      demos: 'العروض التوضيحية:',
      score: 'كيف كان أداؤك؟',
      scoreOk: '✅ صحيح',
      scoreNo: '❌ خطأ / جزئي'
    },
    usage: {
      title: 'طريقة الاستخدام:',
      body: 'حاول صياغة الإجابة قبل الكشف. استخدم التقييم الذاتي لتحديد نقاط الضعف. راجع المفهوم في علامة التبويب'
    }
  },
  about: {
    title: '👤 حول',
    tabs: {
      author: '👤 المؤلف',
      bibliography: '📚 المراجع'
    },
    section: {
      about: 'عن المشروع',
      author: 'المؤلف',
      contribute: 'ساهم — التعليقات والاقتراحات عبر Pull Request',
      repository: 'المستودع',
      network: 'شبكة المواقع'
    },
    project: {
      p1body: 'يهدف هذا المشروع إلى تسهيل تعلّم فيزياء أشباه الموصلات وسيُوسَّع باستمرار مع تقدّم دراستي. بُني باستخدام Claude Opus 4.7 كشريك برمجة ثنائي، مع SWE-1.6 للتعديلات الأقل تعقيدًا، استنادًا إلى قراءاتي لكتاب Semiconductor Physics and Devices لـ Donald A. Neamen، مكملًا بسلسلة Modular Series on Solid State Devices (Robert F. Pierret وآخرون).',
      p2body: 'هذا مشروع غير ربحي يشكّل جزءًا من شبكة مواقع Basicão da Eletrônica، مكرّس لنشر المعرفة الإلكترونية بطريقة ميسّرة ومجانية.'
    },
    authorInfo: {
      name: 'الاسم:',
      website: 'الموقع الإلكتروني:',
      email: 'البريد الإلكتروني:',
      whatsapp: 'واتساب:',
      community: 'مجتمع واتساب — فيزياء أشباه الموصلات:',
      joinGroup: 'الانضمام إلى المجموعة',
      linkedin: 'لينكدإن:',
      github: 'جيت هاب:',
      twitter: 'X (تويتر):'
    },
    pr: {
      heading: '🎯 الرسالة الأساسية:',
      body: 'أفضل طريقة لانتقاد هذا المشروع أو اقتراح تحسين له أو تصحيحه هي فتح Pull Request. مساهمتك مرحّب بها دائمًا، سواء كانت فاصلة في تفسير أو فصلاً كاملاً من التصورات الجديدة. حتى لو لم يُقبل PR كما هو بالضبط، فإنه يفتح نقاشًا قيمًا عادةً ما يتطوّر إلى شيء مُدمج في المشروع.',
      openPR: '🚀 فتح Pull Request',
      guide: '📖 دليل المساهمة (CONTRIBUTING.md)',
      openIssue: '🐛 فتح Issue'
    },
    windsurf: {
      heading: '🌊 استخدم Windsurf — المشروع جاهز فعلاً',
      p1: 'أنصح بشدة باستخدام Windsurf كمحرر للمساهمة. يحتوي المشروع على قواعد ترميز في .windsurf/rules/ يطبّقها المحرر تلقائيًا: نمط التسجيل (PDCL)، تنسيق Markdown، بنية المكوّنات — كلها مُهيّأة. كما تتوفر workflows جاهزة في .windsurf/workflows/ (مثل /pdcl) تؤتمت المهام الشائعة. يفهم عميل Cascade منهجية PDCL (Plan, Do, Check, Loop) المعتمدة هنا ويوجّه التطبيقات الجديدة طبيعيًا ضمن هذا التدفق.',
      p2: 'إذا كنت تفضّل VS Code أو Cursor أو محررًا آخر، فكل شيء يعمل — فقط راعِ القواعد الموضّحة في .windsurf/rules/ يدويًا.'
    },
    hook: {
      heading: '⚙️ Hook pre-commit — توثيق موحّد تلقائيًا',
      body: 'يوجد سكربت آلي في scripts/markdown_history_manager.py يُفعَّل عبر .git/hooks/pre-commit. في كل commit يشمل ملفات .md، يكتشف المؤلف والتاريخ، ويضيف إدخالاً في سجلّ التغييرات وفق المعيار المحدّد في .windsurf/rules/documentacao.md.',
      useAlways: 'استخدمه دائمًا — فهو ما يُبقي التوثيق قابلاً للتتبع وموحّدًا.'
    },
    biblio: {
      heading: 'المراجع الموصى بها',
      intro: 'قائمة منظّمة بـ {count} عنوانًا. الأوّلان هما المرجعان الرئيسيّان المستخدمان في بناء هذا النظام؛ والباقي يكمّل نظرية النطاقات والأجهزة وفيزياء الحالة الصلبة. كل عنصر مرتبط بأمازون (بحث أو منتج مباشر).',
      mainRefs: '📌 المراجع الرئيسية للمشروع',
      supplementary: '📚 المراجع التكميلية',
      amazonBtn: '🛒 أمازون',
      amazonTitle: 'شراء/بحث على أمازون',
      note: 'ملاحظة:',
      noteBody: 'بعض الروابط تؤدي إلى صفحة منتج محددة عند توفّرها على أمازون البرازيل؛ بينما تؤدي الأخرى إلى البحث حسب العنوان/المؤلف، لعرض أقرب طبعة متاحة. قد تكون الطبعات القديمة نافدة — ابحث أيضًا في المكتبات المستعملة، ومكتبات الجامعات، ومنصات مثل Z-Library و IEEE Xplore و Google Books.'
    }
  },
  glossary: {
    title: '📚 المسرد',
    searchPlaceholder: 'ابحث عن مصطلح أو تعريف...',
    noResults: 'لم يُعثر على مصطلح',
    categories: {
      all: 'الكل',
      physics: 'الفيزياء',
      materials: 'المواد',
      devices: 'الأجهزة',
      technology: 'التكنولوجيا'
    }
  },
  support: {
    title: '💖 ادعمنا',
    tabs: {
      about: '👤 حول',
      donation: '💰 التبرعات'
    },
    about: {
      title: 'عن المشروع',
      p1: 'هذا مشروع تعليمي غير ربحي يهدف إلى نشر المعرفة حول فيزياء أشباه الموصلات بطريقة ميسرة ومجانية.',
      p2: 'تطوير هذه المنصة وصيانتها يتطلب وقتًا وموارد وتفانيًا. مساهمتك تساعد في الحفاظ على المشروع نشطًا وتوسيع محتواه.',
      p3: 'جميع التبرعات تُستخدم لتغطية تكاليف الاستضافة وتطوير ميزات جديدة وتحسين النظام باستمرار.',
      highlight: 'معًا، يمكننا جعل التعليم في الفيزياء والهندسة أكثر إتاحة للجميع!',
      howToHelp: 'كيف يمكنك المساعدة:',
      codeContribution: 'ساهم بالكود عبر Pull Requests على GitHub',
      documentation: 'حسّن التوثيق والترجمات',
      translation: 'ترجم المحتوى إلى لغات أخرى',
      reporting: 'أبلغ عن الأخطاء واقترح تحسينات',
      financial: 'قدّم تبرعًا ماليًا لدعم المشروع'
    },
    donation: {
      title: 'قدّم تبرعًا',
      intro: 'اختر طريقة المساهمة التي تفضلها. أي مبلغ مرحب به ويحدث فرقًا!',
      pixScan: 'امسح رمز QR',
      pixKey: 'مفتاح PIX',
      pixNote: 'استخدم المفتاح أعلاه أو امسح رمز QR مع تطبيق البنك',
      crypto: 'العملات المشفرة',
      copy: 'نسخ',
      viewExplorer: 'عرض في المستكشف',
      disclaimer: 'تنبيه مهم',
      disclaimerText: 'التبرعات طوعية وغير قابلة للاسترداد. عند تقديم تبرع، أنت تدعم مشروعًا تعليميًا غير ربحي. اتصل بنا إذا كان لديك أي أسئلة.'
    },
    wallet: {
      title: 'تبرع عبر المحفظة',
      connectDescription: 'قم بتوصيل محفظة العملات المشفرة (MetaMask، WalletConnect، إلخ) للتبرع مباشرة.',
      connecting: 'جاري الاتصال...',
      connectButton: '🦊 توصيل MetaMask',
      wallet: 'المحفظة',
      network: 'الشبكة',
      ethereumMainnet: 'شبكة Ethereum الرئيسية',
      sepoliaTestnet: 'شبكة Sepolia التجريبية',
      chainId: 'معرف السلسلة',
      switchNetwork: 'التبديل إلى شبكة Ethereum الرئيسية',
      crypto: 'العملة المشفرة',
      addressOnly: 'العنوان فقط',
      amount: 'المبلغ',
      suggestedAmounts: 'المبالغ المقترحة',
      donateButton: '💖 تبرع الآن',
      processing: 'جاري المعالجة...',
      transactionSent: 'تم إرسال المعاملة',
      addressDescription: 'للتبرعات بـ {{crypto}}، استخدم العنوان أدناه:',
      disconnect: 'قطع الاتصال',
      copy: 'نسخ'
    }
  }
};
